/**
 * Free, public county parcel services covering the brokerage's MLS footprint
 * (Teton Board of REALTORS feed). Each entry was verified to be a reachable,
 * publicly published government GIS service. Coverage is county-by-county;
 * areas without a verified free service simply show no parcel lines until a
 * paid provider (Regrid) is enabled.
 *
 * `bounds` is a generous [west, south, east, north] used only to skip
 * requests to a county service when the map isn't anywhere near it.
 *
 * Duplicated for the listing detail page map in
 * live/wwwroot/scripts/listing-map.js (plain JS, no build step). Change both.
 */

export type ParcelSourceKind = "featureLayer" | "dynamicMapLayer";

export interface ParcelSource {
  key: string;
  name: string;
  kind: ParcelSourceKind;
  url: string;
  /**
   * Sublayer ids for dynamicMapLayer (MapServer) sources. ParcelLayer redraws
   * each one in PARCEL_STYLE through a dynamicLayers override, so the
   * MapServer root must report supportsDynamicLayers: true.
   */
  layers?: number[];
  /** Optional server-side filter for featureLayer sources (esri-leaflet `where`). */
  where?: string;
  bounds: [number, number, number, number];
  attribution: string;
}

export const PARCEL_SOURCES: ParcelSource[] = [
  {
    // Hosted ArcGIS Online feature service, public + CORS-enabled.
    key: "teton-id",
    name: "Teton County, ID",
    kind: "featureLayer",
    url: "https://services1.arcgis.com/as6biEYkl7PaUM4Y/arcgis/rest/services/TETON_COUNTY_PARCELS_(PUBLIC)/FeatureServer/0",
    bounds: [-111.45, 43.5, -110.9, 44.0],
    attribution: "Teton County, ID GIS",
  },
  {
    // Bonneville County (Idaho Falls) ArcGIS Server MapServer. Its own
    // symbology is #ffaa00; ParcelLayer overrides it via dynamicLayers.
    key: "bonneville-id",
    name: "Bonneville County, ID",
    kind: "dynamicMapLayer",
    url: "https://gis.bonnevillecountyidaho.gov/hosted/rest/services/Parcel_Viewer/MapServer",
    layers: [0],
    bounds: [-112.5, 43.15, -111.15, 43.9],
    attribution: "Bonneville County, ID GIS",
  },
  {
    // Madison County / City of Rexburg MRGIS MapServer. Its own symbology is
    // #ffc814; ParcelLayer overrides it via dynamicLayers.
    key: "madison-id",
    name: "Madison County, ID",
    kind: "dynamicMapLayer",
    url: "https://madison.rexburg.org/mrgis/rest/services/Data/Parcels/MapServer",
    layers: [0],
    bounds: [-112.0, 43.6, -111.35, 44.0],
    attribution: "Madison County / City of Rexburg GIS",
  },
  {
    // Fremont County, ID GIS hosted feature service (Island Park, Ashton,
    // St. Anthony), public + CORS-enabled, republished daily. The previous
    // fremontgis.com MapServer was Fremont County, Colorado.
    key: "fremont-id",
    name: "Fremont County, ID",
    kind: "featureLayer",
    url: "https://services6.arcgis.com/tNgqqEFlzKrVeDPz/arcgis/rest/services/Parcels/FeatureServer/6",
    bounds: [-112.2, 43.85, -111.0, 44.8],
    attribution: "Fremont County, ID GIS",
  },
  {
    // Wyoming statewide parcels (WY Dept. of Revenue / ETS), hosted ArcGIS
    // Online. Filtered to the TBOR Wyoming counties and to valued parcels
    // (actualvalu>0) so Yellowstone/Grand Teton/BLM mega-polygons don't
    // swamp the map. URL is tax-year suffixed: bump to _for_2027 once that
    // service is published with the same jurisdicti/actualvalu fields.
    key: "wy-statewide",
    name: "Wyoming Counties",
    kind: "featureLayer",
    url: "https://services3.arcgis.com/r0iJ85SKZ4zAzz3P/arcgis/rest/services/Wyoming_Parcels_for_2026/FeatureServer/0",
    where: "jurisdicti IN ('TETON','LINCOLN','SUBLETTE','FREMONT','SWEETWATER') AND actualvalu > 0",
    bounds: [-111.06, 40.95, -107.5, 44.66],
    attribution: "Wyoming Dept. of Revenue / ETS",
  },
];

/**
 * Documented coverage gaps (no cleanly usable free public endpoint):
 * - Jefferson County, ID: county GIS server has an invalid TLS certificate
 *   (hostname mismatch); browsers refuse it over HTTPS. No valid-cert hosted
 *   service found.
 * (Teton County, WY is now covered by the Wyoming statewide service above.)
 * These areas simply render no parcel lines (graceful).
 */

/** True when the source's bounds intersect the given map bounds. */
export function sourceInView(
  s: ParcelSource,
  view: { west: number; south: number; east: number; north: number }
): boolean {
  const [w, sLat, e, n] = s.bounds;
  return !(view.west > e || view.east < w || view.south > n || view.north < sLat);
}
